import { useState, useEffect } from 'react';
import type { EventCategory, DateRange, CategoryFilter } from '../types';
import { TOWNS } from '../constants/towns';
import './EventFilters.css';

interface EventFiltersProps {
  categories: EventCategory[];
  dateRange: DateRange;
  categoryFilter: CategoryFilter;
  onDateRangeChange: (range: DateRange) => void;
  onCategoryChange: (categoryId: CategoryFilter) => void;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  townFilter?: string | null;
  onTownChange?: (town: string | null) => void;
}

export function EventFilters({
  categories,
  dateRange,
  categoryFilter,
  onDateRangeChange,
  onCategoryChange,
  searchQuery = '',
  onSearchChange,
  townFilter = null,
  onTownChange,
}: EventFiltersProps) {
  const [searchInput, setSearchInput] = useState(searchQuery);
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange?.(searchInput.trim());
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const from = e.target.value || null;
    onDateRangeChange(
      from && dateRange?.to
        ? { from, to: dateRange.to }
        : from
          ? { from, to: from }
          : null
    );
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const to = e.target.value || null;
    onDateRangeChange(
      to && dateRange?.from
        ? { from: dateRange.from, to }
        : to
          ? { from: to, to }
          : null
    );
  };

  return (
    <section className="event-filters" aria-label="Filter events">
      <div className="event-filters-inner">
        {(onSearchChange || onTownChange) && (
          <div className="event-filters-search-row">
            {onSearchChange && (
              <form className="event-filters-search" onSubmit={handleSearchSubmit}>
                <label htmlFor="filter-search" className="visually-hidden">Search events</label>
                <input
                  id="filter-search"
                  type="search"
                  placeholder="Search events…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="event-filters-search-input"
                />
                <button type="submit" className="event-filters-search-btn">Search</button>
              </form>
            )}
            {onTownChange && (
              <div className="event-filters-towns">
                <span className="event-filters-towns-label">Town</span>
                <select
                  value={townFilter ?? ''}
                  onChange={(e) => onTownChange(e.target.value || null)}
                  className="event-filters-town-select"
                  aria-label="Filter by town"
                >
                  <option value="">All</option>
                  {TOWNS.map((t) => (
                    <option key={t.slug} value={t.slug}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        <div className="event-filters-dates">
          <span className="event-filters-dates-label">Date range</span>
          <div className="event-filters-dates-inputs">
            <div className="filter-group">
              <label htmlFor="filter-from" className="visually-hidden">From</label>
              <input
                id="filter-from"
                type="date"
                value={dateRange?.from ?? ''}
                onChange={handleFromChange}
                aria-label="From date"
              />
            </div>
            <span className="event-filters-sep" aria-hidden="true">→</span>
            <div className="filter-group">
              <label htmlFor="filter-to" className="visually-hidden">To</label>
              <input
                id="filter-to"
                type="date"
                value={dateRange?.to ?? ''}
                onChange={handleToChange}
                aria-label="To date"
              />
            </div>
          </div>
        </div>

        <div className="event-filters-categories">
          <span className="event-filters-categories-label">Category</span>
          <div className="event-filters-pills" role="group" aria-label="Category filter">
            <button
              type="button"
              className={`event-filter-pill ${!categoryFilter ? 'is-active' : ''}`}
              onClick={() => onCategoryChange(null)}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`event-filter-pill event-filter-pill--${c.slug} ${categoryFilter === c.id ? 'is-active' : ''}`}
                onClick={() => onCategoryChange(categoryFilter === c.id ? null : c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
